import React, { useState, useCallback } from 'react';
import {
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Button,
  Table,
  message,
  Row,
  Col,
  Card,
  AutoComplete,
} from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/vi_VN';
import { doctorService, medicineService, prescriptionService } from '../../services';
import { CreatePrescriptionItemRequest, Medicine } from '../../types';
import ProvinceSelector from '../../components/ProvinceSelector';
import WardSelector from '../../components/WardSelector';
import { debounce } from 'lodash';
import './AddPrescriptionPage.css';

const { Option } = Select;
const { TextArea } = Input;

interface MedicineRow extends CreatePrescriptionItemRequest {
  key: string;
}

const routeOptions = [
  { value: 'Uống', label: 'Uống' },
  { value: 'Tiêm', label: 'Tiêm' },
  { value: 'Bôi', label: 'Bôi' },
  { value: 'Nhỏ mắt', label: 'Nhỏ mắt' },
  { value: 'Nhỏ mũi', label: 'Nhỏ mũi' },
  { value: 'Đặt', label: 'Đặt' },
  { value: 'Ngậm', label: 'Ngậm' },
  { value: 'Xịt', label: 'Xịt' },
];

const unitOptions = [
  { value: 'viên', label: 'Viên' },
  { value: 'gói', label: 'Gói' },
  { value: 'ống', label: 'Ống' },
  { value: 'chai', label: 'Chai' },
  { value: 'tuýp', label: 'Tuýp' },
  { value: 'lọ', label: 'Lọ' },
  { value: 'vỉ', label: 'Vỉ' },
  { value: 'hộp', label: 'Hộp' },
];

const AddPrescriptionPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentProvinceCode, setCurrentProvinceCode] = useState<string | undefined>();
  const [medicineRows, setMedicineRows] = useState<MedicineRow[]>([
    {
      key: '1',
      medicine_name: '',
      route: 'Uống',
      morning: 0,
      noon: 0,
      afternoon: 0,
      evening: 0,
      days: 3,
      quantity: 0,
      usage_unit: 'viên',
      display_unit: 'Viên',
      note: '',
    },
  ]);
  const [medicineOptions, setMedicineOptions] = useState<{ value: string; medicine: Medicine }[]>([]);

  const { data: doctors } = useQuery({
    queryKey: ['doctors'],
    queryFn: () => doctorService.getList(),
  });

  const createMutation = useMutation({
    mutationFn: prescriptionService.create,
    onSuccess: () => {
      message.success('Lưu toa thuốc thành công!');
      queryClient.invalidateQueries({ queryKey: ['prescriptions'] });
      navigate('/facility/prescriptions');
    },
    onError: () => {
      message.error('Lưu toa thuốc thất bại!');
    },
  });

  const debouncedSearch = useCallback(
    debounce(async (searchText: string) => {
      if (searchText.length >= 2) {
        const results = await medicineService.search(searchText);
        setMedicineOptions(
          results.map((m) => ({
            value: m.name,
            medicine: m,
          }))
        );
      } else {
        setMedicineOptions([]);
      }
    }, 300),
    []
  );

  const handleMedicineSearch = (searchText: string) => {
    debouncedSearch(searchText);
  };

  const handleMedicineSelect = (value: string, option: any, rowKey: string) => {
    const medicine = option.medicine as Medicine;
    setMedicineRows((prev) =>
      prev.map((row) =>
        row.key === rowKey
          ? {
              ...row,
              medicine_name: medicine.name,
              medicine_id: medicine.id,
              route: medicine.route || row.route,
              usage_unit: medicine.usage_unit || row.usage_unit,
              display_unit: medicine.display_unit || row.display_unit,
            }
          : row
      )
    );
  };

  const handleMedicineNameChange = (value: string, rowKey: string) => {
    setMedicineRows((prev) =>
      prev.map((row) => (row.key === rowKey ? { ...row, medicine_name: value } : row))
    );
  };

  const updateMedicineRow = (key: string, field: keyof MedicineRow, value: any) => {
    setMedicineRows((prev) => {
      const newRows = prev.map((row) => {
        if (row.key === key) {
          const updatedRow = { ...row, [field]: value };
          if (['morning', 'noon', 'afternoon', 'evening', 'days'].includes(field)) {
            const total =
              (updatedRow.morning + updatedRow.noon + updatedRow.afternoon + updatedRow.evening) *
              updatedRow.days;
            updatedRow.quantity = total;
          }
          return updatedRow;
        }
        return row;
      });
      return newRows;
    });
  };

  const addMedicineRow = () => {
    const newKey = String(Date.now());
    setMedicineRows((prev) => [
      ...prev,
      {
        key: newKey,
        medicine_name: '',
        route: 'Uống',
        morning: 0,
        noon: 0,
        afternoon: 0,
        evening: 0,
        days: 3,
        quantity: 0,
        usage_unit: 'viên',
        display_unit: 'Viên',
        note: '',
      },
    ]);
  };

  const removeMedicineRow = (key: string) => {
    if (medicineRows.length > 1) {
      setMedicineRows((prev) => prev.filter((row) => row.key !== key));
    }
  };

  const handleProvinceChange = (value: string) => {
    setCurrentProvinceCode(value);
    form.setFieldValue('ward_code', undefined);
  };

  const calculateMedicineFee = () => {
    return 0;
  };

  const onFinish = (values: any) => {
    const validItems = medicineRows.filter((row) => row.medicine_name.trim() !== '');

    if (validItems.length === 0) {
      message.error('Vui lòng thêm ít nhất một loại thuốc!');
      return;
    }

    const payload = {
      doctor_id: values.doctor_id,
      patient_name: values.patient_name,
      patient_phone: values.patient_phone || '',
      patient_identity: values.patient_identity || '',
      patient_address: values.patient_address,
      province_code: values.province_code,
      ward_code: values.ward_code,
      diagnosis: values.diagnosis,
      advice: values.advice || '',
      days_of_treatment: values.days_of_treatment || 3,
      re_exam_date: values.re_exam_date ? values.re_exam_date.format('YYYY-MM-DD') : undefined,
      consultation_fee: values.consultation_fee || 0,
      medicine_fee: values.medicine_fee || 0,
      items: validItems.map((item, index) => ({
        medicine_id: item.medicine_id,
        medicine_name: item.medicine_name,
        route: item.route,
        morning: item.morning,
        noon: item.noon,
        afternoon: item.afternoon,
        evening: item.evening,
        days: item.days,
        quantity: item.quantity,
        usage_unit: item.usage_unit,
        display_unit: item.display_unit,
        note: item.note || '',
        order: index + 1,
      })),
    };

    createMutation.mutate(payload);
  };

  const columns = [
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_: any, record: MedicineRow) => (
        <Button
          type="link"
          danger
          onClick={() => removeMedicineRow(record.key)}
          disabled={medicineRows.length === 1}
        >
          Xóa
        </Button>
      ),
    },
    {
      title: 'Tên hoạt chất',
      dataIndex: 'medicine_name',
      key: 'medicine_name',
      width: 280,
      render: (_: any, record: MedicineRow) => (
        <AutoComplete
          value={record.medicine_name}
          options={medicineOptions}
          onSearch={handleMedicineSearch}
          onSelect={(value, option) => handleMedicineSelect(value, option, record.key)}
          onChange={(value) => handleMedicineNameChange(value, record.key)}
          placeholder="Nhập tên thuốc"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Đường dùng',
      dataIndex: 'route',
      key: 'route',
      width: 100,
      render: (_: any, record: MedicineRow) => (
        <Select
          value={record.route}
          onChange={(value) => updateMedicineRow(record.key, 'route', value)}
          style={{ width: '100%' }}
        >
          {routeOptions.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Sáng',
      dataIndex: 'morning',
      key: 'morning',
      width: 60,
      render: (_: any, record: MedicineRow) => (
        <InputNumber
          min={0}
          value={record.morning}
          onChange={(value) => updateMedicineRow(record.key, 'morning', value || 0)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Trưa',
      dataIndex: 'noon',
      key: 'noon',
      width: 60,
      render: (_: any, record: MedicineRow) => (
        <InputNumber
          min={0}
          value={record.noon}
          onChange={(value) => updateMedicineRow(record.key, 'noon', value || 0)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Chiều',
      dataIndex: 'afternoon',
      key: 'afternoon',
      width: 60,
      render: (_: any, record: MedicineRow) => (
        <InputNumber
          min={0}
          value={record.afternoon}
          onChange={(value) => updateMedicineRow(record.key, 'afternoon', value || 0)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Tối',
      dataIndex: 'evening',
      key: 'evening',
      width: 60,
      render: (_: any, record: MedicineRow) => (
        <InputNumber
          min={0}
          value={record.evening}
          onChange={(value) => updateMedicineRow(record.key, 'evening', value || 0)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Đơn vị dùng',
      dataIndex: 'usage_unit',
      key: 'usage_unit',
      width: 100,
      render: (_: any, record: MedicineRow) => (
        <Select
          value={record.usage_unit}
          onChange={(value) => updateMedicineRow(record.key, 'usage_unit', value)}
          style={{ width: '100%' }}
        >
          {unitOptions.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Số ngày',
      dataIndex: 'days',
      key: 'days',
      width: 70,
      render: (_: any, record: MedicineRow) => (
        <InputNumber
          min={1}
          value={record.days}
          onChange={(value) => updateMedicineRow(record.key, 'days', value || 1)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Tổng',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 70,
      render: (_: any, record: MedicineRow) => (
        <InputNumber
          min={0}
          value={record.quantity}
          onChange={(value) => updateMedicineRow(record.key, 'quantity', value || 0)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Đơn vị tính',
      dataIndex: 'display_unit',
      key: 'display_unit',
      width: 90,
      render: (_: any, record: MedicineRow) => (
        <Select
          value={record.display_unit}
          onChange={(value) => updateMedicineRow(record.key, 'display_unit', value)}
          style={{ width: '100%' }}
        >
          {unitOptions.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Ghi chú',
      dataIndex: 'note',
      key: 'note',
      render: (_: any, record: MedicineRow) => (
        <Input
          value={record.note}
          onChange={(e) => updateMedicineRow(record.key, 'note', e.target.value)}
          placeholder="Ghi chú"
        />
      ),
    },
  ];

  return (
    <div className="add-prescription-page">
      <h1 className="page-title">Nhập toa thuốc thủ công</h1>

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Card className="form-section">
          <Form.Item
            name="doctor_id"
            label="Bác sĩ kê toa"
            rules={[{ required: true, message: 'Vui lòng chọn bác sĩ!' }]}
          >
            <Select placeholder="Chọn bác sĩ" style={{ width: 300 }}>
              {doctors?.map((doctor) => (
                <Option key={doctor.id} value={doctor.id}>
                  {doctor.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Card>

        <Card title="Thông tin khách hàng" className="form-section">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="patient_name"
                label="Họ và tên"
                rules={[{ required: true, message: 'Vui lòng nhập họ tên!' }]}
              >
                <Input placeholder="Nhập họ và tên" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="patient_identity" label="Mã số thuế / CCCD">
                <Input placeholder="Nhập mã số thuế hoặc CCCD" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="patient_phone" label="Điện thoại">
                <Input placeholder="Nhập số điện thoại" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="province_code" label="Tỉnh/Thành">
                <ProvinceSelector onChange={handleProvinceChange} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="ward_code" label="Phường/Xã">
                <WardSelector provinceCode={currentProvinceCode} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="patient_address"
                label="Địa chỉ"
                rules={[{ required: true, message: 'Vui lòng nhập địa chỉ!' }]}
              >
                <Input placeholder="Số nhà, tên đường" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title="Thông tin toa thuốc"
          className="form-section"
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={addMedicineRow}>
              Thêm thuốc
            </Button>
          }
        >
          <Table
            columns={columns}
            dataSource={medicineRows}
            pagination={false}
            size="small"
            scroll={{ x: 1200 }}
            rowKey="key"
          />
        </Card>

        <Card className="form-section">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="diagnosis"
                label="Chẩn đoán"
                rules={[{ required: true, message: 'Vui lòng nhập chẩn đoán!' }]}
              >
                <TextArea rows={2} placeholder="Nhập chẩn đoán" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="advice" label="Lời dặn">
                <TextArea rows={2} placeholder="Nhập lời dặn" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={4}>
              <Form.Item name="days_of_treatment" label="Ngày thuốc" initialValue={3}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="consultation_fee" label="Tiền khám" initialValue={0}>
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value!.replace(/\$\s?|(,*)/g, '') as any}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="medicine_fee" label="Tiền thuốc" initialValue={0}>
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value!.replace(/\$\s?|(,*)/g, '') as any}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="re_exam_date" label="Ngày tái khám">
                <DatePicker
                  format="DD/MM/YYYY"
                  locale={locale}
                  style={{ width: '100%' }}
                  placeholder="Chọn ngày tái khám"
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <div className="form-actions">
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={createMutation.isPending}
            size="large"
          >
            Lưu toa thuốc
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AddPrescriptionPage;
